import React, { useState, useEffect } from 'react';
import { organizationService, Organization } from '@/firebase/organizationService';
import { getOrganizationAnalytics, OrganizationAnalytics as OrgAnalyticsType, HealthStatus } from '@/services/organizationAnalyticsService';
import { useNotification } from '@/contexts/NotificationContext';
import { Eye, Loader2, BarChart3, Users, MapPin, Activity, RefreshCw } from 'lucide-react';

const healthConfig: Record<HealthStatus, { label: string; bg: string; text: string }> = {
  healthy: { label: 'Healthy', bg: 'bg-green-50', text: 'text-green-700' },
  at_risk: { label: 'At risk', bg: 'bg-amber-50', text: 'text-amber-700' },
  inactive: { label: 'Inactive', bg: 'bg-red-50', text: 'text-red-700' },
};

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
  const diffMins = Math.floor(diffMs / (60 * 1000));
  if (diffDays > 0) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} min ago`;
  return 'Just now';
}

const OrganizationAnalytics: React.FC = () => {
  const { showNotification } = useNotification();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [analytics, setAnalytics] = useState<OrgAnalyticsType | null>(null);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadAnalytics(selectedOrgId);
    } else {
      setAnalytics(null);
    }
  }, [selectedOrgId]);

  const loadOrganizations = async () => {
    setIsLoadingOrgs(true);
    try {
      const result = await organizationService.getAllOrganizations();
      if (result.success && result.data) {
        setOrganizations(result.data);
        if (result.data.length > 0 && !selectedOrgId) {
          const firstId = result.data[0].id ?? '';
          if (firstId) setSelectedOrgId(firstId);
        }
      } else {
        showNotification('Failed to load organizations', 'error');
      }
    } catch (error: any) {
      console.error('Error loading organizations:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const loadAnalytics = async (organizationId: string) => {
    setIsLoadingAnalytics(true);
    try {
      const result = await getOrganizationAnalytics(organizationId);
      if (result.success && result.data) {
        setAnalytics(result.data);
      } else {
        showNotification(result.error || 'Failed to load analytics', 'error');
        setAnalytics(null);
      }
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      showNotification('Failed to load analytics', 'error');
      setAnalytics(null);
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  const handleRefresh = () => {
    if (selectedOrgId) loadAnalytics(selectedOrgId);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Organization Analytics</h2>
        </div>
        {selectedOrgId && (
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoadingAnalytics}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingAnalytics ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Organization
        </label>
        {isLoadingOrgs ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
            <span className="text-sm text-gray-500">Loading organizations...</span>
          </div>
        ) : (
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Select an organization --</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {!selectedOrgId && (
        <p className="text-sm text-gray-500 py-4">Select an organization to view analytics.</p>
      )}

      {selectedOrgId && isLoadingAnalytics && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {selectedOrgId && !isLoadingAnalytics && analytics && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`rounded-lg p-4 border ${healthConfig[analytics.health].bg} border-gray-200`}>
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase">Health</span>
              </div>
              <p className={`font-semibold ${healthConfig[analytics.health].text}`}>
                {healthConfig[analytics.health].label}
              </p>
            </div>
            <div className="rounded-lg p-4 border border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase">Users</span>
              </div>
              <p className="font-semibold text-gray-900">
                {analytics.metrics.activeUsers30d} active / {analytics.metrics.totalUsers} total
              </p>
            </div>
            <div className="rounded-lg p-4 border border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase">Locations</span>
              </div>
              <p className="font-semibold text-gray-900">
                {analytics.metrics.totalLocations}
                {analytics.metrics.maxLocations != null && analytics.metrics.maxLocations > 0
                  ? ` of ${analytics.metrics.maxLocations}`
                  : ''}{' '}
                locations
              </p>
              {analytics.locations?.length > 0 && (
                <p className="text-xs text-gray-500 mt-1" title={analytics.locations.map((l) => l.name).join(', ')}>
                  {analytics.locations.map((l) => l.name).join(', ')}
                </p>
              )}
            </div>
            <div className="rounded-lg p-4 border border-gray-200 bg-gray-50/50">
              <div className="flex items-center gap-2 mb-1">
                <Eye className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-600 uppercase">Last active</span>
              </div>
              <p className="font-semibold text-gray-900">
                {analytics.lastActivityAt
                  ? formatRelativeTime(analytics.lastActivityAt)
                  : 'No activity'}
              </p>
            </div>
          </div>

          {/* Subscription */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/50">
            <p className="text-xs font-medium text-gray-600 uppercase mb-1">Subscription</p>
            <p className="text-sm text-gray-900">
              Plan: <span className="font-medium capitalize">{analytics.subscription.plan}</span>
              {' · '}
              Status: <span className="font-medium capitalize">{analytics.subscription.status}</span>
            </p>
          </div>

          {/* User engagement */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">User engagement (last 30 days)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Active users (30d)</p>
                <p className="text-lg font-semibold text-gray-900">{analytics.metrics.activeUsers30d}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Logins</p>
                <p className="text-lg font-semibold text-gray-900">{analytics.metrics.logins30d}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Pending invites</p>
                <p className="text-lg font-semibold text-gray-900">{analytics.metrics.pendingInvitations}</p>
              </div>
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="text-xs text-gray-500">New users</p>
                <p className="text-lg font-semibold text-gray-900">{analytics.metrics.newUsers30d}</p>
              </div>
            </div>
            <div className="mt-3 rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 mb-1">Checklists completed (30d)</p>
              <p className="text-lg font-semibold text-gray-900">{analytics.metrics.checklistsCompleted30d}</p>
            </div>
          </div>

          {/* Locations list */}
          {analytics.locations?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Locations in this organization</h3>
              <ul className="space-y-1 text-sm text-gray-700 list-disc list-inside">
                {analytics.locations.map((loc) => (
                  <li key={loc.id}>
                    {loc.name}
                    <span className="text-xs text-gray-400 ml-1">(ID: {loc.id})</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Note: Only admins see all locations. Managers and users only see locations they&apos;re assigned to.
              </p>
            </div>
          )}

          {/* Activity summary */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Activity summary</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• {analytics.metrics.auditActions30d} audit actions in the last 30 days</li>
              <li>• {analytics.metrics.checklistsCompleted30d} checklists completed</li>
              <li>• {analytics.metrics.totalAssets} assets across locations</li>
              <li>• {analytics.metrics.totalChecklists} checklist templates</li>
            </ul>
          </div>
        </div>
      )}

      {selectedOrgId && !isLoadingAnalytics && !analytics && (
        <p className="text-sm text-gray-500 py-4">Could not load analytics for this organization.</p>
      )}
    </div>
  );
};

export default OrganizationAnalytics;
